import * as THREE from 'three';
import { Random } from '../core/Random';
import { buildRifle } from './builders/character';
import { neonBeerSign } from './builders/furniture';
import type { CollisionWorld } from './Collision';
import type { Vec2 } from './layout';
import { box, cyl, M, mat, mesh, registerNightGlow, texMat } from './materials';
import { PublicRoom, type RoomKit } from './PublicRoom';
import { BOOZE_SLOT, GAS_SLOT, LAUNDRY_SLOT, PAWN_SLOT } from './rooms';
import {
  linoleumTexture,
  panelingTexture,
  roofTexture,
  shagTexture,
  signTexture,
  woodTexture,
  type SignSpec,
} from './textures';

/**
 * Inside the shops on the strip. Every one of them keeps the help behind something:
 * bulletproof plexiglass at the Gas-N-Grub, steel bars at Lucky's, chicken wire at the
 * Booze Barn, a barred window at the laundromat. Local coordinates; the door is on +z.
 */

/** Where the clerk stands, and where you stand to talk to them through the glass. */
export interface ShopSpots {
  clerk: Vec2;
  window: Vec2;
}

export const GAS_SPOTS = {
  clerk: { x: -4.6, z: -3.0 },
  window: { x: -4.6, z: -0.55 },
  grill: { x: -5.6, z: 2.6 },
  slushie: { x: 5.6, z: -3.6 },
} as const satisfies Record<string, Vec2>;

export const PAWN_SPOTS = {
  clerk: { x: 0, z: -3.0 },
  window: { x: 0, z: -0.55 },
  guitar: { x: -4.6, z: 1.6 },
} as const satisfies Record<string, Vec2>;

export const BOOZE_SPOTS = {
  clerk: { x: 5.0, z: -0.4 },
  window: { x: 3.0, z: -0.4 },
  cooler: { x: -1.5, z: -3.6 },
} as const satisfies Record<string, Vec2>;

export const LAUNDRY_SPOTS = {
  clerk: { x: 5.6, z: -3.4 },
  window: { x: 5.6, z: -1.2 },
  washer: { x: -3.0, z: -3.3 },
  dryer: { x: -0.5, z: -3.3 },
  lostFound: { x: 5.4, z: 1.8 },
  folding: { x: 4.8, z: 0.4 },
  tvSeat: { x: 2.4, z: 3.5 },
  tv: { x: 6.85, z: 3.2 },
  pickles: { x: -2.6, z: 3.5 },
  vending: { x: -6.2, z: 3.3 },
} as const satisfies Record<string, Vec2>;

// --- shared bits -------------------------------------------------------------------------

function sign(spec: SignSpec, w: number, h: number, glow = 0): THREE.Mesh {
  const tex = signTexture(spec);
  const m = texMat(tex, glow > 0 ? { emissive: '#ffffff', emissiveIntensity: glow } : {});
  return mesh(new THREE.PlaneGeometry(w, h), m, 0, 0, 0, { cast: false });
}

/** Fluorescent tube panels on the ceiling. */
function fluorescents(kit: RoomKit, spots: Array<[number, number]>): void {
  const glow = mat('#f4fbff', { emissive: '#eaf6ff', emissiveIntensity: 1.3 });
  for (const [x, z] of spots) {
    kit.ceiling.add(box(1.25, 0.06, 0.4, mat('#d9dde0'), x, kit.shape.height - 0.04, z));
    kit.ceiling.add(box(1.15, 0.02, 0.3, glow, x, kit.shape.height - 0.08, z, { cast: false }));
  }
}

/** Vertical steel bars from `y0` to `y1`, `width` wide along local x. */
function bars(width: number, y0: number, y1: number, spacing = 0.13): THREE.Group {
  const g = new THREE.Group();
  const h = y1 - y0;
  for (let x = -width / 2; x <= width / 2 + 1e-6; x += spacing)
    g.add(cyl(0.012, 0.012, h, M.steel, x, y0 + h / 2, 0, 5));
  g.add(box(width + 0.06, 0.04, 0.04, M.steel, 0, y1 - 0.02, 0));
  g.add(box(width + 0.06, 0.04, 0.04, M.steel, 0, y0 + 0.4, 0));
  return g;
}

const plexiMat = (): THREE.MeshStandardMaterial =>
  mat('#d8eef4', { rough: 0.05, metal: 0.1, transparent: true, opacity: 0.22 });

/** A shelf unit (gondola) along local x, stocked on both faces. */
function gondola(length: number, rng: Random, palette: string[]): THREE.Group {
  const g = new THREE.Group();
  const metal = mat('#c9ccd0', { metal: 0.3, rough: 0.6 });
  g.add(box(length, 1.5, 0.08, metal, 0, 0.75, 0));
  for (const y of [0.12, 0.6, 1.05]) {
    g.add(box(length, 0.04, 0.82, metal, 0, y, 0));
    for (const side of [-1, 1]) {
      let x = -length / 2 + 0.08;
      while (x < length / 2 - 0.12) {
        const w = rng.range(0.12, 0.3);
        const h = rng.range(0.16, 0.36);
        g.add(
          box(w, h, 0.22, mat(rng.pick(palette), { rough: 0.6 }), x + w / 2, y + 0.02 + h / 2, side * 0.25),
        );
        x += w + rng.range(0.01, 0.05);
      }
    }
  }
  return g;
}

/** Glass-door coolers along local x, lit from inside, full of something cold. Faces +z. */
function coolerWall(length: number, rng: Random, palette: string[], cases = false): THREE.Group {
  const g = new THREE.Group();
  const body = mat('#3a3d42', { metal: 0.3 });
  // A shell (back, top, kick plate, ends) so you can see in through the glass.
  g.add(box(length, 2.2, 0.08, body, 0, 1.1, -0.31));
  g.add(box(length, 0.2, 0.7, body, 0, 2.1, 0));
  g.add(box(length, 0.18, 0.7, body, 0, 0.09, 0));
  for (const s of [-1, 1]) g.add(box(0.08, 2.2, 0.7, body, s * (length / 2 - 0.04), 1.1, 0));
  const inside = mat('#f2f8ff', { emissive: '#dff1ff', emissiveIntensity: 0.55 });
  g.add(box(length - 0.18, 1.8, 0.02, inside, 0, 1.1, -0.26, { cast: false }));
  const doors = Math.round(length / 0.9);
  const dw = length / doors;
  for (let i = 0; i < doors; i++) {
    const x = -length / 2 + dw * (i + 0.5);
    for (const y of [0.45, 0.95, 1.45, 1.9]) {
      if (cases) {
        g.add(box(dw - 0.16, 0.3, 0.32, mat(rng.pick(palette), { rough: 0.5 }), x, y - 0.03, 0.02));
      } else {
        for (let k = 0; k < 5; k++)
          g.add(
            cyl(
              0.035,
              0.035,
              0.24,
              mat(rng.pick(palette), { rough: 0.3 }),
              x - dw / 2 + 0.12 + k * ((dw - 0.24) / 4),
              y + 0.02,
              0.05,
              6,
            ),
          );
      }
      g.add(box(dw - 0.06, 0.03, 0.5, mat('#d0d4d8', { metal: 0.4 }), x, y - 0.2, 0.0));
    }
    g.add(box(0.05, 1.95, 0.05, M.chrome, x - dw / 2 + 0.03, 1.08, 0.36));
    g.add(box(0.03, 0.5, 0.06, M.chrome, x + dw / 2 - 0.12, 1.2, 0.41, { cast: false }));
  }
  g.add(box(length - 0.16, 1.9, 0.02, plexiMat(), 0, 1.1, 0.35, { cast: false }));
  return g;
}

function plasticChair(color: string): THREE.Group {
  const g = new THREE.Group();
  const p = mat(color, { rough: 0.5 });
  g.add(box(0.46, 0.05, 0.44, p, 0, 0.44, 0));
  g.add(box(0.46, 0.46, 0.05, p, 0, 0.7, -0.2));
  for (const x of [-0.2, 0.2]) for (const z of [-0.18, 0.18]) g.add(box(0.04, 0.44, 0.04, p, x, 0.22, z));
  return g;
}

// --- the Gas-N-Grub ------------------------------------------------------------------------

export interface GasStore {
  room: PublicRoom;
  /** Hot dogs turning on the roller grill. */
  dogs: THREE.Group;
  /** The bulletproof glass you talk through (outlined when you walk up). */
  glass: THREE.Object3D;
}

export function buildGasInterior(parent: THREE.Object3D, collision: CollisionWorld): GasStore {
  const rng = new Random(4101);
  const dogs = new THREE.Group();
  let window: THREE.Object3D = dogs;
  const room = new PublicRoom(
    parent,
    collision,
    GAS_SLOT,
    'gas',
    {
      wall: mat('#ece6d6', { rough: 0.9 }),
      floor: texMat(linoleumTexture(), { rough: 0.35 }),
      ceiling: mat('#e8e6e0', { rough: 1 }),
    },
    (kit) => {
      const { length: L, depth: D } = kit.shape;
      const snacks = ['#d62828', '#f77f00', '#fcbf49', '#2a9d8f', '#3a86ff', '#8338ec', '#ff006e', '#ffbe0b'];
      const drinks = ['#e63946', '#2a9d8f', '#ffd60a', '#3a86ff', '#7ed957', '#f4a261', '#9b5de5'];
      // The clerk's booth in the back-left corner: counter, plexiglass to the ceiling.
      const cz = -1.4;
      const boothX0 = -L / 2;
      const boothX1 = -2.4;
      const bw = boothX1 - boothX0;
      const bx = (boothX0 + boothX1) / 2;
      kit.add(box(bw, 1.05, 0.7, mat('#7a6a58'), 0, 0.525, 0), bx, cz);
      kit.add(box(bw + 0.04, 0.05, 0.8, mat('#d9cfb8'), 0, 1.075, 0), bx, cz + 0.02);
      kit.solid(bx, cz, bw, 0.8);
      kit.add(
        box(0.1, kit.shape.height, D / 2 + cz, mat('#7a6a58'), 0, kit.shape.height / 2, 0),
        boothX1,
        (cz - D / 2) / 2,
      );
      kit.solid(boothX1, (cz - D / 2) / 2, 0.12, D / 2 + cz);
      const glass = kit.place(
        box(bw, kit.shape.height - 1.1, 0.03, plexiMat(), bx, cz + 0.15),
        bx,
        cz + 0.15,
      );
      glass.position.y = 1.1 + (kit.shape.height - 1.1) / 2;
      window = glass;
      // The money tray through the glass, and "the" pen on a chain.
      kit.add(box(0.36, 0.06, 0.46, M.steel, 0, 1.1, 0), GAS_SPOTS.window.x, cz + 0.05);
      kit.add(cyl(0.006, 0.006, 0.4, M.black, 0, 1.15, 0, 4), GAS_SPOTS.window.x + 0.4, cz + 0.32);
      // Behind the clerk: the cigarette wall and the lottery machine.
      const smokes = ['#c62828', '#ffffff', '#1d4e9e', '#2f7d32', '#ffd400', '#111111'];
      for (let r = 0; r < 5; r++)
        for (let c = 0; c < 14; c++)
          kit.add(
            box(0.16, 0.12, 0.06, mat(rng.pick(smokes)), 0, 1.4 + r * 0.16, 0),
            boothX0 + 0.3 + c * 0.2,
            -D / 2 + 0.12,
          );
      kit.add(box(0.6, 1.3, 0.5, mat('#2a6ad0', { metal: 0.3 }), 0, 0.65, 0), boothX1 - 0.5, -D / 2 + 0.35);
      const lotto = sign(
        {
          w: 512,
          h: 128,
          bg: '#ffd400',
          fg: '#c62828',
          lines: [{ text: 'LOTTO • MEGA BUCKS', size: 64, font: 'Bangers' }],
        },
        1.8,
        0.45,
        0.6,
      );
      lotto.position.set(bx, 2.95, cz + 0.2);
      kit.place(lotto, bx, cz + 0.2);
      lotto.position.y = 2.95;
      const nope = sign(
        {
          w: 512,
          h: 160,
          bg: '#ffffff',
          fg: '#111111',
          border: '#c62828',
          borderWidth: 10,
          lines: [
            { text: 'NO SHIRT NO SHOES', size: 48, font: 'Anton' },
            { text: 'NO PROBLEM', size: 48, font: 'Anton', color: '#c62828' },
          ],
        },
        0.9,
        0.28,
      );
      nope.position.set(bx + 0.9, 1.7, cz + 0.17);
      kit.place(nope, bx + 0.9, cz + 0.17);
      nope.position.y = 1.7;

      // Aisles of snacks.
      for (const z of [-0.6, 1.6]) {
        kit.add(gondola(4.2, rng, snacks), 1.8, z);
        kit.solid(1.8, z, 4.3, 0.85);
      }
      // Drink coolers down the east wall.
      const coolers = coolerWall(6.2, rng, drinks);
      kit.add(coolers, L / 2 - 0.4, -0.6, -Math.PI / 2);
      kit.solid(L / 2 - 0.4, -0.6, 0.75, 6.2);
      // The roller grill (the dogs turn) and the slushie machine.
      const g = GAS_SPOTS.grill;
      kit.add(box(1.4, 0.95, 0.6, mat('#b8bcc0', { metal: 0.4 }), 0, 0.475, 0), g.x, g.z - 0.5);
      kit.solid(g.x, g.z - 0.5, 1.4, 0.6);
      kit.add(box(0.8, 0.08, 0.45, M.steel, 0, 1.0, 0), g.x, g.z - 0.5);
      for (let i = 0; i < 6; i++) {
        const dog = cyl(0.025, 0.025, 0.42, mat(i % 2 ? '#b5542d' : '#c66a3a', { rough: 0.5 }), 0, 0, 0, 8);
        dog.rotation.z = Math.PI / 2;
        dog.position.set(0, 1.07, -0.18 + i * 0.07);
        dogs.add(dog);
      }
      kit.place(dogs, g.x, g.z - 0.5);
      kit.animate((t) => dogs.children.forEach((d, i) => (d.rotation.x = t * 3 + i)));
      const s = GAS_SPOTS.slushie;
      kit.add(box(0.9, 1.0, 0.6, mat('#d9dde0'), 0, 0.5, 0), s.x - 0.2, s.z);
      kit.solid(s.x - 0.2, s.z, 0.9, 0.6);
      for (const [i, c] of ['#ff2e63', '#08d9d6', '#a5ff3a'].entries())
        kit.add(
          box(
            0.24,
            0.5,
            0.3,
            mat(c, { emissive: c, emissiveIntensity: 0.4, transparent: true, opacity: 0.85 }),
            0,
            1.27,
            0,
          ),
          s.x - 0.5 + i * 0.3,
          s.z,
        );
      // An ATM that may or may not work, and a rack of lighters by the door.
      kit.add(box(0.6, 1.6, 0.5, mat('#3b3f44', { metal: 0.4 }), 0, 0.8, 0), 3.6, D / 2 - 0.4);
      kit.solid(3.6, D / 2 - 0.4, 0.6, 0.5);
      kit.add(
        box(0.4, 0.25, 0.02, mat('#7ed957', { emissive: '#4fbf2a', emissiveIntensity: 0.6 }), 0, 1.25, 0),
        3.6,
        D / 2 - 0.66,
      );
      fluorescents(kit, [
        [-4.5, 1.5],
        [-1, -2],
        [1.8, 0.5],
        [4.5, -2],
        [4.5, 2.5],
        [-1, 2.8],
      ]);
      const open = sign(
        {
          w: 256,
          h: 96,
          bg: '#081408',
          fg: '#39ff5a',
          lines: [{ text: 'OPEN 24/7', size: 64, font: 'Bangers' }],
        },
        1.0,
        0.38,
        1.2,
      );
      open.rotation.y = Math.PI;
      open.position.set(-3, 2.2, D / 2 - 0.02);
      kit.walls.front.add(open);
    },
  );
  return { room, dogs, glass: window };
}

// --- Lucky's Pawn & Payday -------------------------------------------------------------------

/** A shop room and the barred window (or glass) you buy through. */
export interface ShopRoom {
  room: PublicRoom;
  window: THREE.Object3D;
}

export function buildPawnInterior(parent: THREE.Object3D, collision: CollisionWorld): ShopRoom {
  const rng = new Random(4202);
  let window: THREE.Object3D = new THREE.Group();
  const room = new PublicRoom(
    parent,
    collision,
    PAWN_SLOT,
    'pawn',
    {
      wall: texMat(panelingTexture(), { color: '#b08860' }),
      floor: texMat(shagTexture('#6b5a48'), { rough: 1 }),
      ceiling: mat('#d9d4c8', { rough: 1 }),
    },
    (kit) => {
      const { length: L, depth: D, height: H } = kit.shape;
      const cz = -1.6;
      // Glass display cases full of gold, then bars from the counter to the ceiling.
      for (const x of [-3.4, -1.15, 1.15, 3.4]) {
        kit.add(box(2.1, 0.95, 0.75, mat('#3a2a1e'), 0, 0.475, 0), x, cz);
        kit.add(box(2.12, 0.04, 0.78, plexiMat(), 0, 0.97, 0), x, cz);
        for (let i = 0; i < 6; i++) {
          const ring = new THREE.Mesh(
            new THREE.TorusGeometry(0.05, 0.012, 6, 12),
            mat(rng.pick(['#e8c55a', '#d9d9d9', '#ffd700']), { metal: 0.9, rough: 0.2 }),
          );
          ring.rotation.x = Math.PI / 2;
          ring.position.set(x - 0.8 + i * 0.32, 0.92, cz + rng.range(-0.15, 0.15));
          kit.add(ring, ring.position.x, ring.position.z);
          ring.position.y = 0.93;
        }
      }
      kit.solid(0, cz, 8.6, 0.8);
      for (const [x, w] of [
        [-2.45, 3.9],
        [2.45, 3.9],
      ] as const)
        kit.add(bars(w, 1.0, H), x, cz + 0.05);
      // The cash window: a gap in the bars with a little ledge.
      kit.add(box(0.95, 0.05, 0.36, M.steel, 0, 1.0, 0), 0, cz + 0.2);
      kit.add(box(1.0, 0.06, 0.06, M.steel, 0, 1.9, 0), 0, cz + 0.05);
      window = kit.place(bars(0.95, 1.9, H, 0.13), 0, cz + 0.05);
      // Back wall: rifles on a rack, guitars, TVs nobody came back for.
      for (let i = 0; i < 6; i++) {
        const r = buildRifle();
        r.rotation.set(0, 0, Math.PI / 2);
        kit.add(r, -4.2 + i * 0.35, -D / 2 + 0.12);
        r.position.y = 1.9;
      }
      kit.add(box(2.4, 0.08, 0.12, mat('#5a3a1e'), 0, 1.45, 0), -3.3, -D / 2 + 0.1);
      for (const [i, c] of ['#c62828', '#1b1b1b', '#e8c55a'].entries()) {
        const gtr = new THREE.Group();
        gtr.add(box(0.36, 0.48, 0.08, mat(c, { rough: 0.3 }), 0, 0, 0));
        gtr.add(box(0.06, 0.6, 0.04, mat('#3a2414'), 0, 0.52, 0));
        gtr.add(box(0.1, 0.12, 0.05, mat('#3a2414'), 0, 0.85, 0));
        kit.add(gtr, 1.3 + i * 0.6, -D / 2 + 0.12);
        gtr.position.y = 1.6;
      }
      for (let i = 0; i < 3; i++)
        for (let j = 0; j < 2 - (i === 2 ? 1 : 0); j++) {
          kit.add(box(0.7, 0.5, 0.5, mat('#2b2b2e'), 0, 0.25 + i * 0.52, 0), 3.6 + j * 0.75, -D / 2 + 0.35);
          kit.add(box(0.56, 0.38, 0.02, M.darkGlass, 0, 0.27 + i * 0.52, 0), 3.6 + j * 0.75, -D / 2 + 0.61);
        }
      const gold = sign(
        {
          w: 512,
          h: 128,
          bg: '#140808',
          fg: '#ffd400',
          lines: [{ text: 'WE BUY GOLD', size: 80, font: 'Bangers' }],
        },
        1.6,
        0.4,
        1.6,
      );
      gold.position.set(0, 2.75, -D / 2 + 0.02);
      kit.walls.back.add(gold);
      // The floor: a used mower, a chainsaw on a crate, a bike, a pink flamingo for sale.
      kit.add(box(0.6, 0.35, 0.9, mat('#c62828'), 0, 0.3, 0), -4.4, 2.6);
      kit.add(box(0.12, 0.7, 0.04, M.black, 0, 0.7, 0), -4.4, 3.1);
      kit.solid(-4.4, 2.6, 0.6, 0.9);
      kit.add(box(0.6, 0.5, 0.5, texMat(woodTexture(), { color: '#a58a5c' }), 0, 0.25, 0), 4.4, 2.6);
      kit.add(box(0.5, 0.2, 0.16, mat('#ff7a1a'), 0, 0.6, 0), 4.4, 2.6);
      kit.add(box(0.3, 0.04, 0.06, M.steel, 0, 0.6, 0), 4.7, 2.6);
      kit.solid(4.4, 2.6, 0.6, 0.5);
      const flam = new THREE.Group();
      flam.add(cyl(0.01, 0.01, 0.6, M.steel, 0, 0.3, 0, 4));
      const body = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 6), mat('#ff6fa8'));
      body.scale.set(1.4, 0.8, 0.8);
      body.position.y = 0.7;
      flam.add(body);
      flam.add(cyl(0.025, 0.025, 0.35, mat('#ff6fa8'), 0.15, 0.95, 0, 6));
      kit.add(flam, 2.6, 3.2, 0.6);
      kit.solid(2.6, 3.2, 0.3, 0.3);
      fluorescents(kit, [
        [-3, -2.6],
        [3, -2.6],
        [-3, 1.6],
        [3, 1.6],
      ]);
      void L;
    },
  );
  return { room, window };
}

// --- the Booze Barn ----------------------------------------------------------------------------

export function buildBoozeInterior(parent: THREE.Object3D, collision: CollisionWorld): ShopRoom {
  const rng = new Random(4303);
  let window: THREE.Object3D = new THREE.Group();
  const room = new PublicRoom(
    parent,
    collision,
    BOOZE_SLOT,
    'booze',
    {
      wall: texMat(woodTexture(), { color: '#9a4a32' }),
      floor: mat('#8d8a84', { rough: 0.95 }),
      ceiling: texMat(woodTexture(), { color: '#7a5038' }),
    },
    (kit) => {
      const { length: L, depth: D, height: H } = kit.shape;
      // Rafters across the barn.
      for (let x = -L / 2 + 1; x < L / 2; x += 2.2)
        kit.ceiling.add(box(0.2, 0.25, D, texMat(woodTexture(), { color: '#5a3a24' }), x, H - 0.4, 0));
      // The walk-in beer cooler: the whole back wall, cases stacked to the ceiling.
      const cases = ['#1d4e9e', '#c9ced3', '#c62828', '#1b1b1b', '#e8c55a', '#2f7d32'];
      kit.add(coolerWall(9, rng, cases, true), -1.5, -D / 2 + 0.45);
      kit.solid(-1.5, -D / 2 + 0.45, 9, 0.75);
      // Liquor on the west wall, locked in a chain-link cage.
      const liquor = ['#5a3a10', '#2f5d2f', '#8a2b22', '#d9c9a0', '#3a3a3a', '#b8860b'];
      const shelf = texMat(woodTexture(), { color: '#6b4a2b' });
      for (const y of [0.5, 1.1, 1.7, 2.3]) {
        kit.add(box(0.45, 0.04, 6, shelf, 0, y, 0), -L / 2 + 0.3, 0);
        for (let z = -2.8; z < 2.8; z += 0.17)
          kit.add(
            cyl(0.05, 0.055, 0.3, mat(rng.pick(liquor), { rough: 0.15 }), 0, y + 0.17, 0, 6),
            -L / 2 + 0.3,
            z,
          );
      }
      kit.solid(-L / 2 + 0.3, 0, 0.5, 6.2);
      const cage = new THREE.Group();
      const wire = mat('#9aa0a6', { metal: 0.6, rough: 0.5, transparent: true, opacity: 0.55 });
      cage.add(box(0.02, 2.6, 6.2, wire, 0, 1.3, 0));
      for (const z of [-3.1, -1.5, 0, 1.5, 3.1]) cage.add(box(0.05, 2.6, 0.05, M.steel, 0, 1.3, z));
      cage.add(box(0.12, 0.2, 0.08, mat('#c9a23a', { metal: 0.8 }), 0.06, 1.2, 0.3));
      kit.add(cage, -L / 2 + 0.75, 0);
      kit.solid(-L / 2 + 0.75, 0, 0.12, 6.2);
      // The register in a chicken-wire booth on the east side.
      const bx = 4.6;
      kit.add(box(0.75, 1.05, 3.4, texMat(woodTexture(), { color: '#8a5a3a' }), 0, 0.525, 0), bx - 1.3, -0.6);
      kit.solid(bx - 1.3, -0.6, 0.8, 3.4);
      const chicken = new THREE.Group();
      chicken.add(box(0.02, H - 1.1, 3.4, wire, 0, 1.1 + (H - 1.1) / 2, 0));
      for (const z of [-1.7, 0, 1.7])
        chicken.add(box(0.06, H - 1.1, 0.06, texMat(woodTexture()), 0, 1.1 + (H - 1.1) / 2, z));
      window = kit.place(chicken, bx - 1.0, -0.6);
      kit.add(box(0.06, 0.4, 0.5, M.black, 0, 1.4, 0), bx - 1.03, BOOZE_SPOTS.window.z);
      kit.solid(bx - 1.0, 1.6, 0.1, 1.2);
      kit.solid(bx - 1.0, -2.3, 0.1, 2.0);
      // A pyramid of 30-racks in the middle of the floor.
      for (let row = 0; row < 4; row++)
        for (let i = 0; i < 4 - row; i++)
          kit.add(
            box(0.55, 0.28, 0.4, mat(rng.pick(cases), { rough: 0.5 }), 0, 0.14 + row * 0.29, 0),
            1.0 - (4 - row - 1) * 0.29 + i * 0.58,
            1.6,
          );
      kit.solid(1.0, 1.6, 2.4, 0.5);
      const neon = neonBeerSign();
      neon.position.set(2.5, 2.4, -D / 2 + 0.02);
      kit.walls.back.add(neon);
      const prices = sign(
        {
          w: 512,
          h: 256,
          bg: '#f4e9cf',
          fg: '#7a1f17',
          border: '#7a1f17',
          borderWidth: 10,
          lines: [
            { text: 'CASE PRICES', size: 70, font: 'Rye' },
            { text: 'NO CHECKS • NO CREDIT • NO KIDDING', size: 32, font: 'Anton', color: '#111' },
          ],
        },
        1.6,
        0.8,
      );
      prices.position.set(-L / 2 + 0.02, 2.8, 0);
      prices.rotation.y = Math.PI / 2;
      kit.walls.west.add(prices);
      // Bare bulbs on cords.
      const bulb = mat('#fff2c0', { emissive: '#ffd27a', emissiveIntensity: 1.6 });
      for (const [x, z] of [
        [-3, -1],
        [1, -1],
        [3.5, 2.5],
        [-3, 2.8],
      ] as const) {
        kit.ceiling.add(cyl(0.008, 0.008, 1.2, M.black, x, H - 0.6, z, 4));
        kit.ceiling.add(
          new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 6), bulb)
            .translateX(x)
            .translateY(H - 1.25)
            .translateZ(z),
        );
      }
      registerNightGlow(bulb, 1.6);
      void roofTexture;
    },
  );
  return { room, window };
}

// --- Suds-N-Duds --------------------------------------------------------------------------------

export interface Laundromat {
  room: PublicRoom;
  window: THREE.Object3D;
  /** The drums of the front loaders (one turns while you're washing). */
  drums: THREE.Mesh[];
  /** The TV bolted up in the corner: its screen. */
  tv: THREE.Mesh;
}

export function buildLaundryInterior(parent: THREE.Object3D, collision: CollisionWorld): Laundromat {
  const rng = new Random(4404);
  const drums: THREE.Mesh[] = [];
  let tv: THREE.Mesh | null = null;
  let laundryWindow: THREE.Object3D = new THREE.Group();
  const room = new PublicRoom(
    parent,
    collision,
    LAUNDRY_SLOT,
    'laundry',
    {
      wall: mat('#cfe3ee', { rough: 0.9 }),
      floor: texMat(linoleumTexture(), { color: '#d8e4ea', rough: 0.4 }),
      ceiling: mat('#e6eaec', { rough: 1 }),
    },
    (kit) => {
      const { length: L, depth: D, height: H } = kit.shape;
      const white = mat('#ecebe6', { rough: 0.5 });
      const clothes = ['#c62828', '#1d4e9e', '#ffd400', '#2f7d32', '#ff6fa8', '#f4f4f0'];
      // Front loaders along the back wall, dryers stacked on top.
      for (let i = 0; i < 9; i++) {
        const x = -L / 2 + 0.8 + i * 0.95;
        if (x > 3.6) break;
        for (const [y, isDryer] of [
          [0, false],
          [1.0, true],
        ] as const) {
          kit.add(box(0.85, 0.95, 0.75, white, 0, y + 0.475, 0), x, -D / 2 + 0.45);
          kit.add(box(0.7, 0.08, 0.02, mat('#3a3d42'), 0, y + 0.85, 0), x, -D / 2 + 0.07 + 0.75);
          const drum = cyl(
            0.27,
            0.27,
            0.02,
            mat(isDryer ? '#3b4a55' : rng.pick(clothes), { rough: 0.6 }),
            0,
            0,
            0,
            14,
          );
          drum.rotation.x = Math.PI / 2;
          drum.position.set(x, y + 0.45, -D / 2 + 0.84);
          kit.place(drum, x, -D / 2 + 0.84);
          drum.position.y = y + 0.45;
          if (!isDryer) drums.push(drum);
          const ring = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.03, 6, 16), M.chrome);
          ring.position.set(x, y + 0.45, -D / 2 + 0.85);
          kit.add(ring, x, -D / 2 + 0.85);
          ring.position.y = y + 0.45;
        }
      }
      kit.solid(-1.2, -D / 2 + 0.45, 8.6, 0.8);
      // An island of top loaders, back to back.
      for (let i = 0; i < 5; i++)
        for (const side of [-1, 1]) {
          const x = -5 + i * 0.85;
          kit.add(box(0.78, 0.95, 0.72, white, 0, 0.475, 0), x, -0.2 + side * 0.38);
          kit.add(box(0.66, 0.03, 0.6, mat('#d9d8d2'), 0, 0.965, 0), x, -0.2 + side * 0.38);
        }
      kit.solid(-3.3, -0.2, 4.4, 1.5);
      // The attendant's office, back right, behind a barred window.
      const ox = 5.6;
      kit.add(box(2.8, H, 0.12, mat('#9fb8c6'), 0, H / 2, 0), ox, -1.8);
      kit.solid(ox, -1.8, 2.8, 0.15);
      kit.add(box(0.12, H, 2.7, mat('#9fb8c6'), 0, H / 2, 0), ox - 1.4, -3.15);
      kit.solid(ox - 1.4, -3.15, 0.15, 2.7);
      kit.add(box(1.0, 0.06, 0.4, M.steel, 0, 1.0, 0), ox, -1.62);
      const win = bars(0.95, 1.05, 2.0, 0.12);
      kit.place(win, ox, -1.72);
      laundryWindow = win;
      // The window is a hole in that wall: cover the wall around it instead.
      const opening = new THREE.Mesh(new THREE.PlaneGeometry(0.95, 0.95), mat('#2a3a44', { rough: 0.4 }));
      opening.position.set(ox, 1.52, -1.86);
      kit.add(opening, ox, -1.86);
      opening.position.y = 1.52;
      // Folding table and the lost & found box.
      const f = LAUNDRY_SPOTS.folding;
      kit.add(box(0.9, 0.06, 2.4, texMat(woodTexture(), { color: '#d8c8a8' }), 0, 0.88, 0), f.x, f.z);
      for (const z of [-1, 1]) kit.add(box(0.06, 0.85, 0.06, M.steel, 0, 0.43, 0), f.x, f.z + z);
      for (let i = 0; i < 4; i++)
        kit.add(
          box(0.4, 0.06, 0.3, mat(rng.pick(clothes)), 0, 0.94 + (i % 2) * 0.06, 0),
          f.x + rng.range(-0.2, 0.2),
          f.z - 0.6 + i * 0.4,
          rng.range(-0.3, 0.3),
        );
      kit.solid(f.x, f.z, 0.9, 2.4);
      const lf = LAUNDRY_SPOTS.lostFound;
      kit.add(box(0.7, 0.5, 0.5, texMat(woodTexture(), { color: '#b58a5a' }), 0, 0.25, 0), lf.x, lf.z);
      for (let i = 0; i < 4; i++)
        kit.add(
          box(0.3, 0.12, 0.25, mat(rng.pick(clothes)), 0, 0.52 + i * 0.05, 0),
          lf.x + rng.range(-0.2, 0.2),
          lf.z + rng.range(-0.1, 0.1),
          rng.range(0, 3),
        );
      kit.solid(lf.x, lf.z, 0.7, 0.5);
      const lfs = sign(
        {
          w: 256,
          h: 96,
          bg: '#ffffff',
          fg: '#1f6fb5',
          lines: [{ text: 'LOST & FOUND', size: 44, font: 'Anton' }],
        },
        0.6,
        0.22,
      );
      kit.place(lfs, lf.x, lf.z + 0.26);
      lfs.position.y = 0.32;
      // Plastic chairs along the front, a vending machine and a TV up in the corner.
      for (let i = 0; i < 5; i++) {
        const x = -3.4 + i * 1.3;
        if (Math.abs(x - kit.shape.doorX) < 0.9) continue;
        kit.add(plasticChair(rng.pick(['#e8642a', '#1f6fb5', '#ffd400'])), x, D / 2 - 0.9, Math.PI);
      }
      const v = LAUNDRY_SPOTS.vending;
      kit.add(box(0.9, 1.8, 0.7, mat('#c62828', { metal: 0.2 }), 0, 0.9, 0), v.x, v.z);
      kit.add(
        box(0.6, 1.2, 0.02, mat('#f2f8ff', { emissive: '#dff1ff', emissiveIntensity: 0.4 }), 0, 1.1, 0),
        v.x + 0.36,
        v.z,
        Math.PI / 2,
      );
      kit.solid(v.x, v.z, 0.9, 0.7);
      const t = LAUNDRY_SPOTS.tv;
      const tvBox = new THREE.Group();
      tvBox.add(box(0.15, 0.6, 0.85, mat('#2b2b2e'), 0, 0, 0));
      const screen = mesh(new THREE.PlaneGeometry(0.72, 0.5), mat('#10181c', { rough: 0.2 }), -0.08, 0, 0, {
        cast: false,
      });
      screen.rotation.y = -Math.PI / 2;
      tvBox.add(screen);
      tvBox.add(box(0.5, 0.06, 0.1, M.steel, 0.15, 0.35, 0));
      kit.place(tvBox, t.x, t.z);
      tvBox.position.y = 2.35;
      tv = screen;
      // Detergent posters and the "not responsible" sign.
      const poster = sign(
        {
          w: 512,
          h: 256,
          bg: '#1f6fb5',
          fg: '#ffffff',
          lines: [
            { text: 'NOT RESPONSIBLE', size: 54, font: 'Anton' },
            { text: 'FOR LOST SOCKS', size: 54, font: 'Anton', color: '#ffd400' },
          ],
        },
        1.4,
        0.7,
      );
      poster.position.set(-L / 2 + 0.02, 2.2, 1.6);
      poster.rotation.y = Math.PI / 2;
      kit.walls.west.add(poster);
      fluorescents(kit, [
        [-4, -1.5],
        [0, -1.5],
        [-4, 2],
        [0, 2],
        [4, 2],
      ]);
      // Turning drums: one in three is going at any given time.
      kit.animate((time) => drums.forEach((d, i) => (d.rotation.y = i % 3 === 0 ? time * 3 + i : 0)));
    },
  );
  if (!tv) throw new Error('laundromat TV missing');
  return { room, drums, tv, window: laundryWindow };
}
